import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { Users, ChartLineUp, Crown, Pulse, Trophy, Sparkle } from "@phosphor-icons/react";

const REFRESH_MS = 30_000;
const EARLY_THRESHOLD = 100; // below this we frame as "first 100" exclusivity

/**
 * Sign-up velocity strip for the landing page.
 *
 * < 100 predictors  → exclusivity-framed early-adopter message
 *                     ("Premier Predictors Cup awaits its first champion" +
 *                      "Be one of the first 100 predictors")
 * >= 100 predictors → real live counters (Predictors / Today / Last hour / Premier)
 *
 * Auto-flips when real signups cross the threshold. No fake numbers.
 */
export const SignupTicker = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await apiClient.get("/stats/signups");
        if (!cancelled) setData(data);
      } catch { /* silent — ticker is a nice-to-have */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, REFRESH_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (!data) return null;
  const { total, today, last_hour: lastHour, premier } = data;

  // Early-adopter framing (honest: hides raw count below threshold)
  if (total < EARLY_THRESHOLD) {
    const remaining = Math.max(0, EARLY_THRESHOLD - total);
    return (
      <div
        data-testid="signup-ticker"
        className="border-y border-primary-yellow/20 bg-gradient-to-r from-[#0B0D11] via-primary-yellow/[0.04] to-[#0B0D11] backdrop-blur"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5">
          <EarlyTile
            icon={<Trophy size={16} weight="fill" className="text-primary-yellow" />}
            headline="Cup Awaits"
            sub="Premier Predictors Cup awaits its first champion"
          />
          <EarlyTile
            icon={<Sparkle size={16} weight="fill" className="text-primary-yellow animate-pulse" />}
            headline="Founders' Window"
            sub={`Be one of the first ${EARLY_THRESHOLD} predictors`}
            highlight
          />
          <EarlyTile
            icon={<Crown size={16} weight="fill" className="text-primary-yellow" />}
            headline="Hall of Champions"
            sub="Etched in PitchIQ history forever"
          />
        </div>
      </div>
    );
  }

  // Threshold crossed — show live counters
  return (
    <div
      data-testid="signup-ticker"
      className="border-y border-white/5 bg-[#0B0D11]/80 backdrop-blur"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
        <Stat
          icon={<Users size={14} weight="fill" className="text-primary-yellow" />}
          value={fmt(total)} label="Predictors"
        />
        <Stat
          icon={<ChartLineUp size={14} weight="fill" className="text-primary-yellow" />}
          value={fmt(today)} label="Joined today"
        />
        <Stat
          icon={<Pulse size={14} weight="bold" className="text-primary-yellow animate-pulse" />}
          value={fmt(lastHour)} label="In the last hour"
          highlight={lastHour > 0}
        />
        <Stat
          icon={<Crown size={14} weight="fill" className="text-primary-yellow" />}
          value={fmt(premier)} label="Premier members"
        />
      </div>
    </div>
  );
};

const EarlyTile = ({ icon, headline, sub, highlight = false }) => (
  <div className={`bg-[#0B0D11] px-4 py-3 flex items-center gap-3 ${highlight ? "ring-1 ring-primary-yellow/40" : ""}`}>
    <span className="shrink-0">{icon}</span>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.22em] text-primary-yellow font-bold">{headline}</div>
      <div className="text-xs text-white/70 mt-0.5 leading-tight">{sub}</div>
    </div>
  </div>
);

const Stat = ({ icon, value, label, highlight = false }) => (
  <div className={`bg-[#0B0D11] px-4 py-2 flex items-center justify-center sm:justify-start gap-3 ${highlight ? "ring-1 ring-primary-yellow/30" : ""}`}>
    <span className="shrink-0">{icon}</span>
    <div className="min-w-0">
      <div className="font-mono-num font-black text-lg leading-none tabular-nums">{value}</div>
      <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mt-1 truncate">{label}</div>
    </div>
  </div>
);

function fmt(n) {
  if (n == null) return "—";
  if (n >= 10000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}

export default SignupTicker;
